"use client";

import type { PlainDate } from "@sa-nu/shared-utils/date";
import { createContext, useContext, type ReactNode } from "react";

import type {
  AccommodationReservationProviderInput,
  StayTicketMasterReferenceInput,
} from "../../../../__generated__/gql/graphql";
import type { NoCancelOptionConfig } from "../../../../constants";
import type { UnavailablePeriod } from "../../dx/ui/date-picker";
import type { SelectedAccommodationReservation } from "../../reservation-form/reservation-calendar/reservation-calendar";
import type { RoomTypeReservationConfirmOptions } from "../reserve/room-type-reservation-calendar";

export type RoomTypeReserveContextValue = {
  provider: AccommodationReservationProviderInput;
  /** 確定金額表示可否の判定に使うStayTicketMasterのID。取得できない場合は null */
  stayTicketMasterId: string | null;
  /**
   * STAY_TICKET_MASTER_CONFIG に一致するときの base オプションの StayTicketMaster 参照。
   * 非 null なら provider ではなく StayTicketMaster 経由で空き状況・見積りを解決する
   */
  stayTicketMasterReference: StayTicketMasterReferenceInput | null;
  /** 現在のStayTicketMasterに対応するキャンセル不可オプション。対象外は null */
  noCancelOptionConfig: NoCancelOptionConfig | null;
  evChargerAvailable: boolean;
  /** ブラウズ日付フィルタで指定済みの初泊日。未指定のときは null */
  firstNight: PlainDate | null;
  /** ブラウズ日付フィルタで指定済みの最終泊日。未指定のときは null */
  lastNight: PlainDate | null;
  /** 泊数下限フロア。指定時はカレンダーの minDate をこの値以降にクランプする */
  minNight?: PlainDate | null;
  /** 滞在可能な最大泊数（StayTicket=束ねたチケット枚数） */
  maxStayNights: number;
  /** カレンダーに注記つきで表示する予約できない期間。未指定なら表示しない */
  unavailablePeriods?: readonly UnavailablePeriod[];
  onDraftSelected: (
    selection: SelectedAccommodationReservation & {
      roomTypeId: string;
    },
    options: RoomTypeReservationConfirmOptions,
  ) => void;
  /** 日程フィルタ（firstNight/lastNight）を確定値として設定する */
  setStayRange: (first: PlainDate, last: PlainDate) => void;
  /** 日程フィルタを解除する */
  clearStayRange: () => void;
  /** エリアフィルタのみを解除する（ci/co・facility 等の他フィルタには触れない） */
  clearAreaFilter: () => void;
};

const RoomTypeReserveContext =
  createContext<RoomTypeReserveContextValue | null>(null);

/**
 * 部屋タイプ予約フローのコンテキスト。null は browsing 専用経路として扱われる。
 */
export function RoomTypeReserveProvider({
  value,
  children,
}: {
  value: RoomTypeReserveContextValue;
  children: ReactNode;
}) {
  return (
    <RoomTypeReserveContext.Provider value={value}>
      {children}
    </RoomTypeReserveContext.Provider>
  );
}

/** null の場合は browsing 専用経路（予約 CTA を出さない）。 */
export function useRoomTypeReserveContext(): RoomTypeReserveContextValue | null {
  return useContext(RoomTypeReserveContext);
}
